import React from "react";

import ace from "ace-builds";
import "ace-builds/src-noconflict/mode-xml";

import { Indent, getIndentString, Editor } from "@tableplus/toolkit";
import { useEditor, EditorIndent, Border } from "@tableplus/toolkit";
import { DivPx, background } from "@moai/core";
import { Prefs } from "prefs/prefs";
import { Toolbar } from "./toolbar/toolbar";
import s from "./edit.module.scss";
import * as xml2js from "xml2js";

// https://github.com/ajaxorg/ace/issues/3913
// eslint-disable-next-line
const workerUrl = require("file-loader!ace-builds/src-noconflict/worker-xml.js");
ace.config.setModuleUrl("ace/mode/xml_worker", workerUrl);

interface Props {
    prefs: Prefs;
    value: string;
    setValue: React.Dispatch<React.SetStateAction<string>>;
}

const xmlParser = new xml2js.Parser({ explicitArray: false });

const xmlFormat = async (value: string, indent?: Indent): Promise<string> => {
    const obj = await xmlParser.parseStringPromise(value);
    const builder = new xml2js.Builder({
        renderOpts: {
            pretty: indent !== undefined,
            indent: indent ? getIndentString(indent) : undefined,
        },
    });
    return builder.buildObject(obj);
};

const removeCommands = [
    "addLineAfter", // ctrl-enter ---> conflict with "format"
    "expandtoline", // ctrl-l ---> browser's default
];

const getEditorIndent = (indent: Indent): EditorIndent => ({
    tab: indent.kind === "tab",
    width: indent.width ?? indent.custom,
});

export const Edit: React.FC<Props> = (props) => {
    const editor = useEditor({
        config: { value: props.value, mode: "ace/mode/xml" },
        removeCommands,
    });

    const {
        onChange: editorOnChange,
        addCommand: editorAddCommand,
        getValue: editorGetValue,
        setValue: editorSetValue,
        setIndent: editorSetIndent,
    } = editor;

    const { setValue: propsSetValue } = props;

    React.useEffect(() => {
        return editorOnChange((params) => propsSetValue(params.value));
    }, [editorOnChange, propsSetValue]);

    const { indent } = props.prefs;
    const format = React.useCallback(async () => {
        try {
            const next = await xmlFormat(editorGetValue() ?? "", indent);
            editorSetValue(next);
            editorSetIndent(getEditorIndent(indent));
        } catch (e) {
            window.alert(`Cannot format: ${e.message}`);
        }
    }, [editorGetValue, editorSetValue, editorSetIndent, indent]);

    React.useEffect(() => {
        const key = { mac: "cmd-enter", win: "ctrl-enter" };
        return editorAddCommand({ key, name: "format", fn: format });
    }, [editorAddCommand, format]);

    const uglify = React.useCallback(async () => {
        try {
            const next = await xmlFormat(editorGetValue() ?? "");
            editorSetValue(next);
        } catch (e) {
            window.alert(`Cannot format: ${e.message}`);
        }
    }, [editorGetValue, editorSetValue]);

    const toolbar = (
        <Toolbar
            getValue={editorGetValue}
            uglify={uglify}
            format={format}
            prefs={props.prefs}
        />
    );
    return (
        <div className={`${s.container} ${background.secondary}`}>
            <DivPx size={16} />
            <div className={s.toolbar}>{toolbar}</div>
            <DivPx size={16} /> <Border color="strong" /> <DivPx size={16} />
            <div className={s.editor}>
                <Editor editor={editor} />
            </div>
        </div>
    );
};
